import { useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, KeyRound, Loader2, Mail, Smartphone, Trash2 } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { CountrySelect, COUNTRIES, type Country } from "../ui/CountrySelect";

// Self-serve account deletion on the web, for people who no longer have the
// app installed. The user proves they own the account (SMS code, or email +
// password for email accounts), then the SAME app-delete-account function the
// app uses erases everything. No request queue, no human approval: identity is
// the only gate. Sign-ins here never create an account (shouldCreateUser:false)
// and the session lives in memory only (persistSession:false on the client).

type Method = "phone" | "email";
type Step = "identify" | "code" | "confirm" | "deleting" | "done";

const inputCls =
  "w-full rounded-2xl border-[3px] border-navy bg-white px-4 py-3 text-navy placeholder-charcoal/45 outline-none transition focus:ring-2 focus:ring-teal focus:ring-offset-2 focus:ring-offset-white";
const labelCls = "mb-1.5 block font-display text-sm font-bold text-navy";

/** Pull the server's own reason out of a failed function invocation. */
async function invokeErrorMessage(err: unknown): Promise<string> {
  try {
    const ctx = (err as { context?: Response } | null)?.context;
    if (ctx && typeof ctx.json === "function") {
      const body = (await ctx.json()) as { error?: string; message?: string; msg?: string } | null;
      const reason = body?.error ?? body?.message ?? body?.msg;
      if (reason) return reason;
    }
  } catch {
    // no body to read: fall through
  }
  if (err instanceof Error && /fetch|network/i.test(err.message)) {
    return "We couldn't reach FitBuddy. Check your connection and try again. Nothing has been deleted.";
  }
  return "That didn't go through. Your account and data are untouched, so please try again.";
}

export function WebDeleteAccount() {
  const [method, setMethod] = useState<Method>("phone");
  const [step, setStep] = useState<Step>("identify");
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [armText, setArmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Digits only, capped at 15 (the E.164 maximum).
  const nationalDigits = phone.replace(/\D/g, "");
  const national = country.iso === "IT" ? nationalDigits : nationalDigits.replace(/^0+/, "");
  const e164 = `${country.dial}${national}`;
  const phoneValid = national.length >= 6 && national.length + country.dial.length - 1 <= 15;
  const armed = armText.trim().toUpperCase() === "DELETE";

  if (!supabase) {
    return (
      <div className="card-pop p-6 sm:p-8">
        <p className="text-charcoal/80">
          Web deletion isn't available right now. You can still delete your
          account instantly from the app: open Settings, then Delete account
          &amp; data.
        </p>
      </div>
    );
  }
  const sb = supabase;

  function switchMethod(m: Method) {
    setMethod(m);
    setError(null);
    setStep("identify");
    setCode("");
  }

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    if (!phoneValid) {
      setError("Enter the mobile number you sign in to FitBuddy with.");
      return;
    }
    setBusy(true);
    setError(null);
    const { error: otpError } = await sb.auth.signInWithOtp({
      phone: e164,
      options: { shouldCreateUser: false },
    });
    setBusy(false);
    if (otpError) {
      // Same failure mapping as the app (weigh-earn-app/lib/otp.ts). The raw
      // error goes to the console, never on screen.
      const msg = otpError.message ?? "";
      const status = (otpError as { status?: number }).status ?? 0;
      const wait = msg.match(/after (\d+) seconds?/i);
      console.warn("delete-account sms send failed:", status, (otpError as { code?: string }).code, msg);
      if (msg.includes("sms_number_unreachable")) {
        setError("We couldn't reach that number by text. Check it's a mobile number and the country is right, or use email and password if your account has one.");
      } else if (msg.includes("sms_provider_unavailable")) {
        setError("We can't send text codes right now. It's on our side, not your number. Use email and password if your account has one, or try again in a little while.");
      } else if (wait) {
        setError(`That's a few codes in a row. Wait ${wait[1]} seconds, then try again.`);
      } else if (/signups? not allowed|not found|user/i.test(msg)) {
        setError("We couldn't find a FitBuddy account for that number. Check the country code and number.");
      } else if (otpError.name === "AuthRetryableFetchError" && !status) {
        setError("You're offline. Check your connection, then try again.");
      } else {
        setError("We can't send text codes right now. It's on our side, not your number. Try again in a little while.");
      }
      return;
    }
    setStep("code");
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the six-digit code we just texted you.");
      return;
    }
    setBusy(true);
    setError(null);
    const { error: verifyError } = await sb.auth.verifyOtp({ phone: e164, token: code, type: "sms" });
    setBusy(false);
    if (verifyError) {
      setError("That code didn't match. Check the six digits, or go back and send a fresh one.");
      return;
    }
    setStep("confirm");
  }

  async function signInWithEmail(e: FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || !password) {
      setError("Enter the email address and password you sign in to FitBuddy with.");
      return;
    }
    setBusy(true);
    setError(null);
    const { error: signInError } = await sb.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setBusy(false);
    if (signInError) {
      setError("That email and password didn't match a FitBuddy account.");
      return;
    }
    setStep("confirm");
  }

  async function deleteAccount() {
    setStep("deleting");
    setError(null);
    const { data, error: invokeError } = await sb.functions.invoke("app-delete-account");
    if (invokeError || !(data as { ok?: boolean } | null)?.ok) {
      setError(await invokeErrorMessage(invokeError));
      setStep("confirm");
      return;
    }
    await sb.auth.signOut().catch(() => {});
    setPassword("");
    setStep("done");
  }

  async function cancel() {
    await sb.auth.signOut().catch(() => {});
    setStep("identify");
    setCode("");
    setArmText("");
    setPassword("");
    setError(null);
  }

  if (step === "done") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-pop flex flex-col items-center px-6 py-12 text-center sm:px-8"
        role="status"
      >
        <span className="flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-navy bg-teal-mist text-teal-deep shadow-pop-xs">
          <CheckCircle2 className="h-9 w-9" aria-hidden="true" />
        </span>
        <h3 className="display mt-6 text-2xl text-navy">Your account is deleted.</h3>
        <p className="mt-3 max-w-md leading-relaxed text-charcoal/75">
          Your sign-in and everything tied to it have been erased. If you still
          have the app installed, you'll be signed out next time you open it.
          Thanks for giving FitBuddy a go.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="card-pop p-6 sm:p-8">
      {step === "identify" || step === "code" ? (
        <div role="tablist" aria-label="How you sign in" className="mb-6 grid grid-cols-2 gap-2">
          {(
            [
              { id: "phone", label: "Phone", icon: Smartphone },
              { id: "email", label: "Email", icon: Mail },
            ] as const
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={method === id}
              onClick={() => switchMethod(id)}
              className={`flex cursor-pointer items-center justify-center gap-2 rounded-2xl border-[3px] border-navy px-3 py-2.5 text-sm font-bold transition ${
                method === id ? "bg-navy text-white" : "bg-white text-navy hover:bg-teal-mist"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      ) : null}

      <AnimatePresence mode="wait" initial={false}>
        {method === "phone" && step === "identify" && (
          <motion.form key="phone" onSubmit={sendCode} noValidate {...fade}>
            <label htmlFor="delete-phone" className={labelCls}>
              The mobile number on your account
            </label>
            <div className="flex gap-2">
              <CountrySelect value={country} onChange={setCountry} />
              <input
                id="delete-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                value={phone}
                maxLength={18}
                onChange={(e) => {
                  setPhone(e.target.value.replace(/[^\d\s]/g, ""));
                  setError(null);
                }}
                placeholder="07123 456789"
                className={`${inputCls} min-w-0 flex-1`}
              />
            </div>
            <SubmitButton busy={busy} label="Text me a code" icon={Smartphone} />
          </motion.form>
        )}

        {method === "phone" && step === "code" && (
          <motion.form key="code" onSubmit={verifyCode} noValidate {...fade}>
            <label htmlFor="delete-code" className={labelCls}>
              Six-digit code sent to {country.dial} {national}
            </label>
            <input
              id="delete-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                setError(null);
              }}
              placeholder="123456"
              className={`${inputCls} font-mono tracking-[0.3em]`}
            />
            <SubmitButton busy={busy} label="Verify code" icon={KeyRound} />
            <button
              type="button"
              onClick={() => {
                setStep("identify");
                setCode("");
                setError(null);
              }}
              className="mt-3 w-full cursor-pointer text-center text-sm font-medium text-teal-deep hover:underline"
            >
              Use a different number
            </button>
          </motion.form>
        )}

        {method === "email" && step === "identify" && (
          <motion.form key="email" onSubmit={signInWithEmail} noValidate {...fade}>
            <label htmlFor="delete-email" className={labelCls}>
              Email
            </label>
            <input
              id="delete-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              placeholder="you@example.com"
              className={inputCls}
            />
            <label htmlFor="delete-password" className={`${labelCls} mt-4`}>
              Password
            </label>
            <input
              id="delete-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              className={inputCls}
            />
            <SubmitButton busy={busy} label="Confirm it's me" icon={KeyRound} />
          </motion.form>
        )}

        {(step === "confirm" || step === "deleting") && (
          <motion.div key="confirm" {...fade}>
            <h3 className="font-display text-lg font-bold text-navy">
              You're signed in. Last check.
            </h3>
            <p className="mt-2 leading-relaxed text-charcoal/80">
              Deleting is immediate and can't be undone. Your account, weigh-ins,
              logs, photos and everything listed above will be erased.
            </p>
            <label htmlFor="delete-arm" className={`${labelCls} mt-5`}>
              Type DELETE to unlock the button
            </label>
            <input
              id="delete-arm"
              type="text"
              autoComplete="off"
              value={armText}
              onChange={(e) => setArmText(e.target.value)}
              placeholder="DELETE"
              className={inputCls}
            />
            <button
              type="button"
              disabled={!armed || step === "deleting"}
              onClick={deleteAccount}
              className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border-[3px] border-navy bg-coral px-5 py-3 font-display font-bold text-navy shadow-pop-xs transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {step === "deleting" ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              )}
              {step === "deleting" ? "Deleting…" : "Permanently delete my account"}
            </button>
            <button
              type="button"
              disabled={step === "deleting"}
              onClick={cancel}
              className="mt-3 w-full cursor-pointer text-center text-sm font-medium text-teal-deep hover:underline disabled:cursor-not-allowed"
            >
              Cancel and keep my account
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <p role="alert" className="mt-4 rounded-xl bg-coral/15 px-4 py-3 text-sm font-medium text-navy">
          {error}
        </p>
      )}
    </div>
  );
}

const fade = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.2 },
};

function SubmitButton({
  busy,
  label,
  icon: Icon,
}: {
  busy: boolean;
  label: string;
  icon: typeof Smartphone;
}) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="btn-primary mt-5 w-full cursor-pointer justify-center px-6 py-3 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Icon className="h-4 w-4" aria-hidden="true" />
      )}
      {label}
    </button>
  );
}
